import { useMemo, useState } from 'react';
import { PersonQueries } from '@frontend/api-person';
import { AutoMessageDetailAppointmentAccordionBody } from '@frontend/auto-message-detail-appointment-event';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { NotificationBadge, styles, Tabs, useAlert } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import { useTempApptInfoPanelContext } from '../../../views/schedule-side-panels';
import { useApptPanelContext } from './appt-panel-context';

export const PanelTabs = () => {
  const { t } = useTranslation('schedule');
  const alerts = useAlert();
  const [selectedTab, setSelectedTab] = useState<string>('notes');
  const { getCFLocationMap } = useSchedulingLocationInfo();
  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const { apptDetails, personInfo, locationId } = useApptPanelContext();
  const personWeaveHouseholdId = personInfo?.WeaveHouseholdID || '';
  const { renderApptInfoPanelContent } = useTempApptInfoPanelContext();

  const { data: householdNotes } = PersonQueries.useGetHouseholdNotes(personWeaveHouseholdId, {
    onError: () => alerts.error(t('Failed to load notes.')),
  });
  const householdNotesCount = householdNotes?.length || 0;

  const formsCFLocationMap = getCFLocationMap('forms');
  const isFormsCFEnabledForAnyLocation = locationId
    ? formsCFLocationMap[locationId]
    : Object.values(formsCFLocationMap).some((value) => !!value);
  const hasApptReminderFlag = getCustomizationFlagValue('apptreminders-automated');
  const showAutoMessagesTab = !!apptDetails?.personInfo && hasApptReminderFlag;

  const shouldShowTabContent = !!personInfo && renderApptInfoPanelContent !== undefined;

  const tabContentDetails: Parameters<typeof renderApptInfoPanelContent>['1'] = useMemo(
    () => ({
      locationId,
      personInfo,
      apptReferenceId: apptDetails?.appointmentDetails?.referenceId || '',
      isPaymentTabActive: selectedTab === 'payments',
    }),
    [apptDetails?.id, locationId, personInfo, selectedTab]
  );

  if (!shouldShowTabContent) {
    return null;
  }

  return (
    <Tabs controlledTab={selectedTab} onChange={setSelectedTab}>
      <Tabs.Bar fullWidth>
        <Tabs.Tab id='notes'>
          <div css={[styles.flexCenter, { gap: theme.spacing(1) }]}>
            {t('Notes')}
            {!!householdNotesCount && (
              <NotificationBadge css={{ background: theme.colors.primary50 }}>{householdNotesCount}</NotificationBadge>
            )}
          </div>
        </Tabs.Tab>
        {showAutoMessagesTab && <Tabs.Tab id='auto-messages'>{t('Auto Messages')}</Tabs.Tab>}
        {isFormsCFEnabledForAnyLocation && <Tabs.Tab id='forms'>{t('Forms')}</Tabs.Tab>}
        <Tabs.Tab id='payments'>{t('Payments')}</Tabs.Tab>
      </Tabs.Bar>
      <Tabs.Panel controller='notes'>
        {selectedTab === 'notes' && renderApptInfoPanelContent('notes', tabContentDetails)}
      </Tabs.Panel>
      {showAutoMessagesTab && (
        <Tabs.Panel controller='auto-messages'>
          {selectedTab === 'auto-messages' && (
            <AutoMessageDetailAppointmentAccordionBody
              appointmentId={apptDetails?.id || ''}
              appointmentLocationId={locationId}
              person={apptDetails?.personInfo}
            />
          )}
        </Tabs.Panel>
      )}
      {isFormsCFEnabledForAnyLocation && (
        <Tabs.Panel controller='forms' css={{ background: theme.colors.neutral10 }}>
          {selectedTab === 'forms' && renderApptInfoPanelContent('forms', tabContentDetails)}
        </Tabs.Panel>
      )}
      <Tabs.Panel controller='payments'>
        {selectedTab === 'payments' && renderApptInfoPanelContent('payments', tabContentDetails)}
      </Tabs.Panel>
    </Tabs>
  );
};
